import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useQuery, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { GET_INVOICE_BY_ID, GET_ALL_INVOICES } from '../graphql/query';
import { UPDATE_INVOICE } from '../graphql/mutation';
import { Invoice } from '../types/interfaces';
import { StatusBadge, CurrencyDisplay } from './common';
import TaskLineTypeFields, { taskLineYupSchema, emptyTaskLine, useDefaultLineType } from './TaskLineTypeFields';
import { getInvoiceStatus } from '../utils/invoiceStatus';

interface InvoiceDetailDialogProps {
  invoiceId: string | null;
  open: boolean;
  onClose: () => void;
}

const validationSchema = yup.object({
  customerName: yup.string().required('Required'),
  customerABN: yup.string().required('Required'),
  customerAddress: yup.string().required('Required'),
  customerEmail: yup.string().email('Invalid email').optional(),
  discount: yup.number().min(0).max(100).required(),
  taxRate: yup.number().min(0).max(100).required(),
  tasks: yup.array().of(taskLineYupSchema).min(1, 'At least one task is required'),
});

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    display="block"
    sx={{ mb: 1, fontWeight: 600 }}
  >
    {children}
  </Typography>
);

const InvoiceDetailDialog: React.FC<InvoiceDetailDialogProps> = ({
  invoiceId,
  open,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const defaultLineType = useDefaultLineType();

  const { data, loading, error } = useQuery(GET_INVOICE_BY_ID, {
    variables: { id: invoiceId },
    skip: !invoiceId || !open,
  });

  const [updateInvoice, { loading: saving }] = useMutation(UPDATE_INVOICE);

  const invoice: Invoice | undefined = data?.invoiceById;

  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerName: invoice?.customerName ?? '',
      customerABN: invoice?.customerABN ?? '',
      customerAddress: invoice?.customerAddress ?? '',
      customerEmail: invoice?.customerEmail ?? '',
      projectName: invoice?.projectName ?? '',
      projectDescription: invoice?.projectDescription ?? '',
      notes: invoice?.notes ?? '',
      discount: invoice?.discount ?? 0,
      taxRate: invoice ? invoice.taxRate * 100 : 10,
      tasks:
        invoice?.tasks?.map((t) => ({
          name: t.name,
          description: t.description,
          lineType: t.lineType,
          duration: t.duration,
          amount: t.amount,
        })) ?? [emptyTaskLine(defaultLineType)],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateInvoice({
          refetchQueries: [
            { query: GET_ALL_INVOICES },
            { query: GET_INVOICE_BY_ID, variables: { id: invoiceId } },
          ],
          variables: {
            id: invoiceId,
            input: {
              customerName: values.customerName,
              customerABN: values.customerABN,
              customerAddress: values.customerAddress,
              customerEmail: values.customerEmail || null,
              projectName: values.projectName || null,
              projectDescription: values.projectDescription || null,
              notes: values.notes || null,
              discount: values.discount,
              taxRate: values.taxRate / 100,
              tasks: values.tasks,
            },
          },
        });
        enqueueSnackbar('Invoice updated successfully', { variant: 'success' });
        setIsEditing(false);
      } catch (err) {
        enqueueSnackbar(
          err instanceof Error ? err.message : 'Failed to update invoice',
          { variant: 'error' }
        );
      }
    },
  });

  const handleCancelEdit = () => {
    formik.resetForm();
    setIsEditing(false);
  };

  const handleAddTask = () => {
    formik.setFieldValue('tasks', [
      ...formik.values.tasks,
      emptyTaskLine(defaultLineType),
    ]);
  };

  const handleRemoveTask = (index: number) => {
    const tasks = [...formik.values.tasks];
    tasks.splice(index, 1);
    formik.setFieldValue('tasks', tasks);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  const renderSkeleton = () => (
    <DialogContent>
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} height={40} sx={{ mb: 1 }} />
      ))}
    </DialogContent>
  );

  const renderViewMode = () => {
    if (!invoice) return null;
    const subtotal = invoice.tasks?.reduce((sum, t) => sum + t.total, 0) ?? invoice.amount;
    const discountAmt = subtotal * (invoice.discount / 100);
    const afterDiscount = subtotal - discountAmt;
    const taxAmt = afterDiscount * invoice.taxRate;
    const total = afterDiscount + taxAmt;

    return (
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid size={6}>
            <SectionLabel>Customer</SectionLabel>
            <Typography variant="body2" fontWeight={600}>
              {invoice.customerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ABN: {invoice.customerABN}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.customerAddress}
            </Typography>
            {invoice.customerEmail && (
              <Typography variant="body2" color="text.secondary">
                {invoice.customerEmail}
              </Typography>
            )}
          </Grid>

          <Grid size={6}>
            <SectionLabel>Project</SectionLabel>
            <Typography variant="body2" fontWeight={600}>
              {invoice.projectName || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.projectDescription || '-'}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <SectionLabel>Tasks</SectionLabel>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.tasks ?? []).map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>{task.description}</TableCell>
                    <TableCell align="right">
                      {task.lineType === 'FIXED' ? '—' : task.duration}
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyDisplay amount={task.total} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <SectionLabel>Financial</SectionLabel>
            <Box display="flex" justifyContent="flex-end">
              <Box sx={{ minWidth: 280 }}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    <CurrencyDisplay amount={subtotal} />
                  </Typography>
                </Box>
                {invoice.discount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Discount ({invoice.discount}%)
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      −<CurrencyDisplay amount={discountAmt} />
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    GST ({(invoice.taxRate * 100).toFixed(0)}%)
                  </Typography>
                  <Typography variant="body2">
                    <CurrencyDisplay amount={taxAmt} />
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700}>
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    <CurrencyDisplay amount={total} />
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={6}>
            <Typography variant="body2" color="text.secondary">
              Invoice Date: {formatDate(invoice.invoiceDate)}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="body2" color="text.secondary">
              Due Date: {formatDate(invoice.dueDate)}
            </Typography>
          </Grid>

          {invoice.notes && (
            <>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <SectionLabel>Notes</SectionLabel>
                <Typography variant="body2">{invoice.notes}</Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
    );
  };

  const renderEditMode = () => (
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid size={12}>
          <SectionLabel>Customer</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer Name"
            name="customerName"
            value={formik.values.customerName}
            onChange={formik.handleChange}
            error={formik.touched.customerName && Boolean(formik.errors.customerName)}
            helperText={formik.touched.customerName && formik.errors.customerName}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer ABN"
            name="customerABN"
            value={formik.values.customerABN}
            onChange={formik.handleChange}
            error={formik.touched.customerABN && Boolean(formik.errors.customerABN)}
            helperText={formik.touched.customerABN && formik.errors.customerABN}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Customer Address"
            name="customerAddress"
            multiline
            rows={2}
            value={formik.values.customerAddress}
            onChange={formik.handleChange}
            error={formik.touched.customerAddress && Boolean(formik.errors.customerAddress)}
            helperText={formik.touched.customerAddress && formik.errors.customerAddress}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer Email"
            name="customerEmail"
            type="email"
            value={formik.values.customerEmail}
            onChange={formik.handleChange}
            error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
            helperText={formik.touched.customerEmail && formik.errors.customerEmail}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Project</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Project Name"
            name="projectName"
            value={formik.values.projectName}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Project Description"
            name="projectDescription"
            value={formik.values.projectDescription}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Tasks</SectionLabel>
        </Grid>

        {formik.values.tasks.map((task, index) => (
          <React.Fragment key={index}>
            <Grid size={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Task {index + 1}</Typography>
                {index > 0 && (
                  <IconButton size="small" onClick={() => handleRemoveTask(index)}>
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Task Name"
                name={`tasks[${index}].name`}
                value={task.name}
                onChange={formik.handleChange}
                error={
                  formik.touched.tasks?.[index]?.name &&
                  Boolean((formik.errors.tasks?.[index] as any)?.name)
                }
                helperText={
                  formik.touched.tasks?.[index]?.name &&
                  (formik.errors.tasks?.[index] as any)?.name
                }
              />
            </Grid>
                <TaskLineTypeFields
                  index={index}
                  lineType={task.lineType ?? 'HOURLY'}
                  duration={task.duration ?? 0}
                  amount={task.amount ?? 0}
                  onChange={formik.handleChange}
                  touched={formik.touched.tasks?.[index]}
                  errors={formik.errors.tasks?.[index] as any}
                />
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                name={`tasks[${index}].description`}
                value={task.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.tasks?.[index]?.description &&
                  Boolean((formik.errors.tasks?.[index] as any)?.description)
                }
                helperText={
                  formik.touched.tasks?.[index]?.description &&
                  (formik.errors.tasks?.[index] as any)?.description
                }
              />
            </Grid>
          </React.Fragment>
        ))}

        <Grid size={12}>
          <Button startIcon={<AddIcon />} onClick={handleAddTask} variant="outlined">
            Add Task
          </Button>
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Financial</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Discount (%)"
            name="discount"
            type="number"
            value={formik.values.discount}
            onChange={formik.handleChange}
            error={formik.touched.discount && Boolean(formik.errors.discount)}
            helperText={formik.touched.discount && formik.errors.discount}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Tax Rate / GST (%)"
            name="taxRate"
            type="number"
            value={formik.values.taxRate}
            onChange={formik.handleChange}
            error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
            helperText={formik.touched.taxRate && formik.errors.taxRate}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Notes</SectionLabel>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            multiline
            rows={3}
            value={formik.values.notes}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>
    </DialogContent>
  );

  const titleText = invoice?.invoiceNumber || (invoiceId ? `#${invoiceId.substring(0, 8).toUpperCase()}` : '');

  return (
    <Dialog
      open={open}
      onClose={isEditing ? undefined : onClose}
      disableEscapeKeyDown={isEditing}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h6" component="span">
            {titleText}
          </Typography>
          {invoice && <StatusBadge status={getInvoiceStatus(invoice)} size="small" />}
        </Box>
        {!isEditing && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {loading && renderSkeleton()}
      {error && (
        <DialogContent>
          <Alert severity="error">Failed to load invoice: {error.message}</Alert>
        </DialogContent>
      )}
      {!loading && !error && (isEditing ? renderEditMode() : renderViewMode())}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEditing ? (
          <>
            <Button onClick={handleCancelEdit} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={() => formik.handleSubmit()}
              variant="contained"
              disabled={saving || !formik.isValid}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="secondary" variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => setIsEditing(true)}
              variant="contained"
              startIcon={<EditIcon />}
              disabled={loading || !!error}
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDetailDialog;
